import { useEffect, useState } from "react";
import { Link } from "react-router";
import { usePageTitle } from "../../hooks/usePageTitle";
import { validateContactMessage } from "../../utils/validation";

interface ContactMessage {
  id: number;
  customer_id: number | null;
  customer_email: string;
  first_name: string | null;
  last_name: string | null;
  subject: string;
  message: string;
  order_number: string | null;
  created_at: string;
  status: "new" | "read" | "replied";
  admin_reply: string | null;
}

export default function ContactMessages() {
  usePageTitle("Contact Messages");
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState("");
  const limit = 20;

  useEffect(() => {
    fetchMessages();
  }, [page, statusFilter]);

  const fetchMessages = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (statusFilter) params.append("status", statusFilter);

      const response = await fetch(`/api/admin/contact-messages?${params}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateMessageStatus = async (messageId: number, status: "new" | "read" | "replied") => {
    try {
      const response = await fetch(`/api/admin/contact-messages/${messageId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        fetchMessages();
        if (selectedMessage && selectedMessage.id === messageId) {
          setSelectedMessage({ ...selectedMessage, status });
        }
      }
    } catch (error) {
      console.error("Error updating message status:", error);
    }
  };

  const submitReply = async () => {
    if (!selectedMessage) return;

    // Validate reply with comprehensive validation
    const validationError = validateContactMessage(replyText);
    if (validationError) {
      setReplyError(validationError);
      return;
    }

    setReplying(true);
    setReplyError("");
    try {
      const response = await fetch(`/api/admin/contact-messages/${selectedMessage.id}/reply`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reply: replyText }),
      });

      if (response.ok) {
        setReplyText("");
        setSelectedMessage(null);
        fetchMessages();
      } else {
        const errorData = await response.json();
        setReplyError(errorData.error || "Failed to send reply");
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
      setReplyError("An unexpected error occurred");
    } finally {
      setReplying(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "new":
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">New</span>;
      case "read":
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">Read</span>;
      case "replied":
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Replied</span>;
      default:
        return null;
    }
  };

  const getCustomerName = (message: ContactMessage) => {
    if (message.first_name && message.last_name) {
      return `${message.first_name} ${message.last_name}`;
    }
    return message.customer_email;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Contact Messages</h1>
        <div className="text-sm text-gray-600">
          {total} total messages
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6 p-5">
        <div className="flex items-center gap-4">
          <label htmlFor="statusFilter" className="text-sm font-medium">
            Filter by Status:
          </label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="input"
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
          </select>
        </div>
      </div>

      {/* Messages List */}
      <div className="card p-5">
        {loading ? (
          <div className="text-center py-12">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-gray-600">
            No messages found
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Date</th>
                    <th className="text-left py-3 px-2">Customer</th>
                    <th className="text-left py-3 px-2">Subject</th>
                    <th className="text-left py-3 px-2">Order</th>
                    <th className="text-left py-3 px-2">Status</th>
                    <th className="text-left py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((message) => (
                    <tr key={message.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-2 text-gray-600 text-sm">
                        {formatDate(message.created_at)}
                      </td>
                      <td className="py-3 px-2">
                        {message.customer_id ? (
                          <Link
                            to="/admin/users"
                            className="text-black underline hover:no-underline"
                          >
                            {getCustomerName(message)}
                          </Link>
                        ) : (
                          <span>{getCustomerName(message)}</span>
                        )}
                      </td>
                      <td className="py-3 px-2 font-medium">{message.subject}</td>
                      <td className="py-3 px-2 text-gray-600 text-sm">
                        {message.order_number ? (
                          <Link
                            to="/admin/orders"
                            className="text-black underline hover:no-underline"
                          >
                            {message.order_number}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-3 px-2">{getStatusBadge(message.status)}</td>
                      <td className="py-3 px-2">
                        <button
                          onClick={() => {
                            setSelectedMessage(message);
                            setReplyText(message.admin_reply || "");
                            if (message.status === "new") {
                              updateMessageStatus(message.id, "read");
                            }
                          }}
                          className="text-sm text-black underline hover:no-underline"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{selectedMessage.subject}</h2>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>{getCustomerName(selectedMessage)}</span>
                    <span>•</span>
                    <span>{formatDate(selectedMessage.created_at)}</span>
                    {selectedMessage.order_number && (
                      <>
                        <span>•</span>
                        <Link
                          to="/admin/orders"
                          className="text-black underline hover:no-underline"
                        >
                          Order {selectedMessage.order_number}
                        </Link>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedMessage(null);
                    setReplyText("");
                  }}
                  className="text-gray-500 hover:text-black text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Status Badge */}
              <div className="mb-6">
                {getStatusBadge(selectedMessage.status)}
              </div>

              {/* Customer Message */}
              <div className="card bg-gray-50 mb-6">
                <h3 className="font-bold mb-3">Customer Message</h3>
                <p className="text-gray-800 whitespace-pre-wrap">
                  {selectedMessage.message}
                </p>
              </div>

              {/* Admin Reply Section */}
              <div className="card bg-blue-50">
                <h3 className="font-bold mb-3">Admin Response</h3>

                {selectedMessage.admin_reply && selectedMessage.status === "replied" ? (
                  <div className="mb-4 p-4 bg-white rounded border border-blue-200">
                    <p className="text-sm text-gray-600 mb-2">Previous Reply:</p>
                    <p className="text-gray-800 whitespace-pre-wrap">
                      {selectedMessage.admin_reply}
                    </p>
                  </div>
                ) : null}

                <textarea
                  value={replyText}
                  onChange={(e) => {
                    setReplyText(e.target.value);
                    setReplyError(""); // Clear error on input
                  }}
                  placeholder="Type your response to the customer..."
                  className="input w-full"
                  rows={6}
                  disabled={replying}
                />

                {replyError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                    {replyError}
                  </div>
                )}

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={submitReply}
                    disabled={replying || !replyText.trim()}
                    className="btn-primary disabled:opacity-50"
                  >
                    {replying ? "Sending..." : "Send Reply"}
                  </button>
                  {selectedMessage.status !== "read" && !selectedMessage.admin_reply && (
                    <button
                      onClick={() => updateMessageStatus(selectedMessage.id, "read")}
                      className="btn-secondary"
                    >
                      Mark as Read
                    </button>
                  )}
                </div>
              </div>

              {/* Customer Info */}
              <div className="card bg-gray-50 mt-6">
                <h3 className="font-bold mb-3">Customer Information</h3>
                <div className="text-sm space-y-2">
                  <div>
                    <span className="text-gray-600">Email:</span>{" "}
                    <a
                      href={`mailto:${selectedMessage.customer_email}`}
                      className="text-black underline hover:no-underline"
                    >
                      {selectedMessage.customer_email}
                    </a>
                  </div>
                  {selectedMessage.customer_id && (
                    <div>
                      <Link
                        to="/admin/users"
                        className="text-black underline hover:no-underline"
                      >
                        View Customer Profile
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
